/**
 * Teams Context Detection Module
 * Detects if the app is running inside Microsoft Teams and initializes TeamsJS
 */

import * as microsoftTeams from '@microsoft/teams-js'

/** Hosting context enumeration */
export type HostingContext = 'teams' | 'browser'

/** Result of Teams context detection */
export interface TeamsContextResult {
  /** Whether the app is running inside Teams */
  isTeams: boolean
  /** The hosting context (teams or browser) */
  hostingContext: HostingContext
  /** Teams context if available */
  context?: microsoftTeams.app.Context
  /** Login hint from Teams context (user's email) */
  loginHint?: string
}

/** Quick check if we're in an iframe (potential Teams context) */
function isInIframe(): boolean {
  try {
    return window !== window.parent
  } catch {
    // Cross-origin restriction means we're definitely in an iframe
    return true
  }
}

/**
 * Initializes TeamsJS with a timeout
 * @param timeoutMs - Timeout in milliseconds (default 2000ms)
 * @returns Promise that resolves when TeamsJS is initialized or rejects on timeout
 */
async function initializeTeamsWithTimeout(timeoutMs: number = 2000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('TeamsJS initialization timed out'))
    }, timeoutMs)

    microsoftTeams.app
      .initialize()
      .then(() => {
        clearTimeout(timeoutId)
        resolve()
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

/**
 * Detects if the app is running inside Microsoft Teams and retrieves context
 * This must be called BEFORE initializing MSAL for NAA to work correctly
 * @returns TeamsContextResult with detection results
 */
export async function detectTeamsContext(): Promise<TeamsContextResult> {
  // Quick heuristic: if not in iframe, definitely not in Teams
  if (!isInIframe()) {
    console.log('[Auth] Not in iframe, using browser context')
    return {
      isTeams: false,
      hostingContext: 'browser',
    }
  }

  // Try to initialize TeamsJS
  try {
    console.log('[Auth] Detected iframe, attempting TeamsJS initialization...')
    await initializeTeamsWithTimeout(2000)

    // Get Teams context
    const context = await microsoftTeams.app.getContext()

    console.log('[Auth] TeamsJS initialized successfully, running in Teams context')

    return {
      isTeams: true,
      hostingContext: 'teams',
      context,
      loginHint: context.user?.loginHint || context.user?.userPrincipalName,
    }
  } catch (error) {
    // TeamsJS initialization failed - we're in an iframe but not Teams
    console.log('[Auth] TeamsJS initialization failed, using browser context:', error)
    return {
      isTeams: false,
      hostingContext: 'browser',
    }
  }
}
